import { Plan, Voucher } from '@accedo/one-plugin-sdk';
import { t } from 'i18next';
import { FC, FormEvent, useEffect, useRef, useState } from 'react';

import { TInputState } from '#/components/Form/Form.types';
import FormInput from '#/components/Form/FormInput';
import {
  getVoucherInputField,
  PlanPickerMainText,
} from '#/plugins/paywall/components';
import { translateDiscount } from '#/plugins/paywall/utils';
import { noop } from '#/service/utils';

import { ReceiptButton, VoucherForm, VoucherFormSubmitButton } from './styled';

interface Props extends Plan {
  onPriceChange: (price: string) => void;
  onVoucherChange: (planId: string, voucherCode: string) => Promise<Voucher>;
}

export const Discount: FC<Props> = ({
  id,
  price,
  currency,
  onPriceChange,
  onVoucherChange,
}) => {
  const [inputState, setInputState] = useState<TInputState>({ value: '' });
  const [voucher, setVoucher] = useState<Voucher>();
  const [voucherError, setVoucherError] = useState<string>();
  const [showVoucherForm, setShowVoucherForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    onPriceChange(voucher?.discountedPrice || price);
  }, [price, voucher, onPriceChange]);

  const updateInputState = (_: string, state: TInputState) => {
    setInputState((prevState) => ({ ...prevState, ...state }));
    setVoucherError(undefined);
  };

  const submitVoucherCode = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);
    onVoucherChange(id, inputState.value as string)
      .then((res) => {
        setVoucher(res);
        setShowVoucherForm(false);
      })
      .catch(() => {
        setVoucherError(t('iap.voucher.error.message'));
      })
      .finally(() => setIsLoading(false));
  };

  // TODO: key vs dataKey
  const { key, label, variant } = getVoucherInputField();

  if (voucher) {
    return (
      <PlanPickerMainText>
        {translateDiscount(currency, voucher)}
      </PlanPickerMainText>
    );
  }

  if (showVoucherForm) {
    return (
      <VoucherForm onSubmit={submitVoucherCode} ref={formRef}>
        <FormInput
          dataKey={key}
          errorMessages={voucherError ? [voucherError] : undefined}
          label={label}
          state={inputState}
          updateErrorMessages={noop}
          updateState={updateInputState}
          variant={variant}
        />

        <VoucherFormSubmitButton
          data-testid="voucher-form-submit"
          disabled={
            isLoading || !inputState.value || !formRef.current?.checkValidity()
          }
          isLoading={isLoading}
          type="submit"
        >
          {t('button.apply')}
        </VoucherFormSubmitButton>
      </VoucherForm>
    );
  }

  return (
    <ReceiptButton
      data-testid="voucher-receipt-button"
      onClick={() => setShowVoucherForm(true)}
    >
      {t('iap.voucher.apply')}
    </ReceiptButton>
  );
};
